import { Component, ChangeDetectionStrategy, inject } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { PopupBenefitComponent } from './shared/popup-benefit.component';
import { PopupBenefitDividerComponent } from './shared/popup-benefit-divider.component';
import { BreakpointObserver } from '@angular/cdk/layout';
import { map } from 'rxjs';
import { AsyncPipe, NgTemplateOutlet } from '@angular/common';

@Component({
  imports: [PopupBenefitComponent, PopupBenefitDividerComponent, NgTemplateOutlet, AsyncPipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (orientation$ | async; as orientation) {
      <div class="popup popup--dark">
        <div class="popup__background">
          <div class="header-container">
            <div class="header-container__panel">
              <div class="popup__heading color-go-green">Green and Easy Loan Management Awaits</div>
              <div class="popup__subheading">Embrace sustainability</div>
              <a class="btn-go-green" href="/loanmanager/go-green/">Go Green</a>
            </div>

            <app-popup-benefit-divider [direction]="orientation === 'vertical' ? 'horizontal' : 'vertical'" />

            <div class="header-container__panel">
              <div class="popup__heading color-autopay">Enjoy Hassle-Free Payments Through</div>
              <a class="btn-autopay" href="/loanmanager/eftsetuprequest/">Auto Pay</a>
              <div class="popup__subheading">The Future is Now!</div>
            </div>
          </div>

          @if (orientation === 'vertical') {
            <app-popup-benefit-divider direction="horizontal" />
          }

          <div class="popup__subheading">
            Reasons to Combine <span class="color-go-green">Go Green</span> with <span class="color-autopay">Auto Pay</span>
          </div>

          <div class="benefits-container">
            @if (orientation === 'vertical') {
              <div class="benefits-container__vertical-subdivider">
                <div class="benefits-container__items">
                  <ng-container *ngTemplateOutlet="effortless"></ng-container>
                  <app-popup-benefit-divider direction="vertical" cssClass="color-go-green" />
                  <ng-container *ngTemplateOutlet="savings"></ng-container>
                  <app-popup-benefit-divider direction="vertical" cssClass="color-go-green" />
                  <ng-container *ngTemplateOutlet="punctuality"></ng-container>
                </div>
                <div class="benefits-container__items">
                  <ng-container *ngTemplateOutlet="ecoConscious"></ng-container>
                  <app-popup-benefit-divider direction="vertical" cssClass="color-autopay" />
                  <ng-container *ngTemplateOutlet="customization"></ng-container>
                </div>
              </div>
            } @else {
              <div class="benefits-container__items">
                <ng-container *ngTemplateOutlet="effortless"></ng-container>
                <app-popup-benefit-divider direction="vertical" cssClass="color-go-green" />
                <ng-container *ngTemplateOutlet="savings"></ng-container>
                <app-popup-benefit-divider direction="vertical" cssClass="color-go-green" />
                <ng-container *ngTemplateOutlet="punctuality"></ng-container>
                <app-popup-benefit-divider direction="vertical" cssClass="color-autopay" />
                <ng-container *ngTemplateOutlet="ecoConscious"></ng-container>
                <app-popup-benefit-divider direction="vertical" cssClass="color-autopay" />
                <ng-container *ngTemplateOutlet="customization"></ng-container>
              </div>
            }
          </div>
        </div>
        <div class="popup__bottom-container">
          <p>
            <a href (click)="close($event)">Not at this time</a>
          </p>
        </div>
      </div>
    }
    <ng-template #effortless>
      <app-popup-benefit
        src="/media/static/app/browser/assets/popups/auto-pay-go-green-combined/effortless.svg"
        heading="Effortless"
        text="Set it and forget it. Enjoy automated monthly payments"
      />
    </ng-template>

    <ng-template #savings>
      <app-popup-benefit
        src="/media/static/app/browser/assets/popups/auto-pay-go-green-combined/savings.svg"
        heading="Savings and Simplicity"
        text="No additional charges and no added stress"
      />
    </ng-template>

    <ng-template #punctuality>
      <app-popup-benefit
        src="/media/static/app/browser/assets/popups/auto-pay-go-green-combined/punctuality.svg"
        heading="Punctuality"
        text="Never miss a due date again"
      />
    </ng-template>

    <ng-template #ecoConscious>
      <app-popup-benefit
        src="/media/static/app/browser/assets/popups/auto-pay-go-green-combined/eco-conscious.svg"
        heading="Eco-Conscious"
        text="Play your part in promoting a cleaner Earth"
      />
    </ng-template>

    <ng-template #customization>
      <app-popup-benefit
        src="/media/static/app/browser/assets/popups/auto-pay-go-green-combined/customization.svg"
        heading="Customization"
        text="Select and day from the 1st to the 15th of each month for your Auto Pay deduction"
      />
    </ng-template>
  `,
  styles: [
    `
      .popup__background {
        background-image: url('/media/static/app/browser/assets/popups/auto-pay-go-green-combined/bg-vertical.png');
        background-position: center;

        @media screen and (min-width: 600px) {
          background-image: url('/media/static/app/browser/assets/popups/auto-pay-go-green-combined/bg-horizontal.png');
        }
      }

      .popup__subheading {
        margin-left: 25px;
        margin-right: 25px;
      }

      .header-container {
        display: flex;
        flex-direction: column;

        @media screen and (min-width: 600px) {
          flex-direction: row;
          margin-bottom: 1em;
        }

        &__panel {
          text-align: center;
        }
      }

      .benefits-container {
        &__items {
          display: flex;
          flex-direction: row;
          margin: 10px;

          app-popup-benefit {
            flex: 1;
          }
        }

        &__vertical-subdivider {
          display: flex;
          flex-direction: column;
        }
      }
    `,
  ],
})
export class AutopayGoGreenCombinedPopupComponent {
  private dialogRef = inject<MatDialogRef<AutopayGoGreenCombinedPopupComponent>>(MatDialogRef);
  breakpointObserver = inject(BreakpointObserver);

  public orientation$ = this.breakpointObserver
    .observe('(max-width: 599px)')
    .pipe(map((result) => (result.matches ? 'vertical' : 'horizontal')));
  close(event: MouseEvent) {
    event.preventDefault();
    this.dialogRef.close();
  }
}
